import * as ms from 'milliseconds';
import React, { useEffect } from 'react';
import { LayoutAnimation, StyleProp, ViewStyle } from 'react-native';
import ChatLoadingIcon from '@shared/assets/icons/chat/loading.svg';
import ChatWaitingToSendIcon from '@shared/assets/icons/chat/waitingToSend.svg';
import CheckmarkFilledIcon from '@shared/assets/icons/checkmark/filled.svg';
import CrossFilledIcon from '@shared/assets/icons/cross/filled.svg';
import { MBView } from '@shared/components/MBView';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import {
  MBAnimationBounceIn,
  MBAnimationFadeIn,
  MBAnimationSpin,
} from '@shared/components/animation';
import { MBImage } from '@shared/components/image';
import { Colors, SharedStyles } from '@shared/styles';
import { usePrevious } from '@shared/util/hooks';
import { Styles } from './styles';

interface MBMessageDeliveryStatusProps {
  status: MBDeliveryStatus | null;
  isEdited?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

interface MBMessageDeliveryStatusContentProps {
  text?: string;
  icon: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/*
    Component to show a message's delivery status. When the status changes, we perform
    a layout animation to smoothly transition. Possible statuses:
      * sending or uploadingAttachments (spinning almost closed blue circle)
      * failed (red X with "Failed" text)
      * waiting (semitransparent circle with "Waiting" text)
      * sent (blue checkmark with "Sent" text)
*/
export function MBMessageDeliveryStatus({
  status,
  isEdited,
  style,
  testID,
}: MBMessageDeliveryStatusProps) {
  const prevStatus = usePrevious(status);

  useEffect(() => {
    if (!!LayoutAnimation) {
      if (!!status !== !!prevStatus) {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      }
    }
  }, [status, prevStatus]);

  if (status === 'sending' || status === 'uploadingAttachments') {
    return (
      <MBMessageDeliveryStatusContent
        style={style}
        icon={
          <MBAnimationSpin duration={ms.seconds(1)} style={SharedStyles.fill}>
            <MBImage fill tintColor={Colors.v2.green} source={ChatLoadingIcon} />
          </MBAnimationSpin>
        }
      />
    );
  }

  if (status === 'failed') {
    return (
      <MBMessageDeliveryStatusContent
        style={style}
        text="Failed to send"
        icon={
          <MBAnimationBounceIn style={SharedStyles.fill}>
            <MBImage fill tintColor={Colors.v2.red} source={CrossFilledIcon} />
          </MBAnimationBounceIn>
        }
      />
    );
  }

  if (status === 'waiting') {
    return (
      <MBMessageDeliveryStatusContent
        style={[style, Styles.deliveryStatusIconWaitingToSend]}
        text="Waiting to send"
        icon={
          <MBAnimationBounceIn style={SharedStyles.fill}>
            <MBImage fill tintColor={Colors.v2.green} source={ChatWaitingToSendIcon} />
          </MBAnimationBounceIn>
        }
      />
    );
  }

  if (status === 'sent') {
    const text = `${isEdited ? '(edited) • ' : ''}Delivered`;

    return (
      <MBMessageDeliveryStatusContent
        style={style}
        text={text}
        icon={
          <MBAnimationBounceIn style={SharedStyles.fill}>
            <MBImage fill tintColor={Colors.v2.green} source={CheckmarkFilledIcon} />
          </MBAnimationBounceIn>
        }
        testID={testID}
      />
    );
  }

  return null;
}

function MBMessageDeliveryStatusContent({
  text,
  icon,
  style,
  testID,
}: MBMessageDeliveryStatusContentProps) {
  return (
    <MBView row center style={[Styles.deliveryStatusContent, style]} testID={testID}>
      {!!text && (
        <MBAnimationFadeIn>
          <View marginRight={0.5}>
            <Text size="caption2">{text}</Text>
          </View>
        </MBAnimationFadeIn>
      )}
      <MBView style={Styles.deliveryStatusIcon}>{icon}</MBView>
    </MBView>
  );
}
